// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useEffect, useState } from 'react';
import {
  intelligenceAdminApi,
  type ChatUseCaseDefault,
  type ChatUseCaseOverride,
} from '../../api/intelligenceAdminApi';
import OverrideEditModal from './OverrideEditModal';

interface Props {
  useCase: ChatUseCaseDefault;
}

const OverridesTable: React.FC<Props> = ({ useCase }) => {
  const [overrides, setOverrides] = useState<ChatUseCaseOverride[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ChatUseCaseOverride | null>(null);
  const [newBrokerageId, setNewBrokerageId] = useState('');

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await intelligenceAdminApi.listOverrides(useCase.code);
      setOverrides(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  // reload is a stable closure over useCase.code; the dep we actually care
  // about is the use-case switch.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [useCase.code]);

  const startNew = async () => {
    const id = newBrokerageId.trim();
    if (!id) return;
    try {
      const fresh = await intelligenceAdminApi.getOverride(useCase.code, id);
      setEditing(fresh);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load brokerage');
    }
  };

  return (
    <div>
      {error && <div style={{ color: '#b91c1c', marginBottom: 8 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input type="text" placeholder="Brokerage UUID to add/edit" value={newBrokerageId}
          onChange={(e) => setNewBrokerageId(e.target.value)}
          style={{ flex: 1, border: '1px solid #d1d5db', padding: '6px 8px', borderRadius: 4 }}
          data-testid="override-brokerage-input"/>
        <button type="button" onClick={startNew} data-testid="override-add">
          Add / edit override
        </button>
      </div>

      {loading ? <div>Loading overrides…</div> : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="overrides-table">
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              <th style={th}>Brokerage</th>
              <th style={th}>Provider</th>
              <th style={th}>Model</th>
              <th style={th}>Voice</th>
              <th style={th}>Enabled</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {overrides.length === 0 && (
              <tr><td colSpan={6} style={{ ...td, color: '#6b7280', fontStyle: 'italic' }}>No overrides — every brokerage uses the platform default</td></tr>
            )}
            {overrides.map(o => (
              <tr key={o.id ?? o.brokerageId} style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={td}><code style={{ fontSize: 12 }}>{o.brokerageId}</code></td>
                <td style={td}>{o.llmProviderOverride ?? '∅'}</td>
                <td style={td}>{o.modelOverride ?? '∅'}</td>
                <td style={td}>{o.voiceEnabledOverride === undefined ? '∅' : (o.voiceEnabledOverride ? 'on' : 'off')}</td>
                <td style={td}>{o.enabled === undefined ? '∅' : (o.enabled ? 'yes' : 'no')}</td>
                <td style={td}>
                  <button type="button" onClick={() => setEditing(o)}>Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <OverrideEditModal
          useCase={useCase}
          brokerageId={editing.brokerageId}
          override={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}/>
      )}
    </div>
  );
};

const th: React.CSSProperties = { textAlign: 'left', padding: '8px 12px', fontWeight: 600, fontSize: 13 };
const td: React.CSSProperties = { padding: '8px 12px', fontSize: 13 };

export default OverridesTable;
